import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import SelectPlaceType           from '@selects/map/SelectPlaceType';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { buildLayerLabels }      from '@/TokamapEditor/helpers/planLayerMapping';
import { previewKey }            from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanCategory }     from '@/TokamapEditor/helpers/planLayerMapping';
import { shapeOwner }            from '@/TokamapEditor/helpers/planShapes';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import planImportStore           from '@/TokamapEditor/planImport/planImportStore';
import { readableLayerName }     from '@/TokamapEditor/planImport/planLayerNames';

const LIST_MAX_HEIGHT = 300;

type Props = {
	previewed: string | null;
	onPreview: (key: string) => void;
};

const PlanLayerList = observer(({ previewed, onPreview }: Props) => {
	const tokens = useDesignTokens();
	const [ownType, setOwnType] = React.useState<Set<string>>(new Set());

	const detection = planImportStore.detection;
	const categories = planImportStore.effectiveCategories;
	const sharedType = planImportStore.sharedPlaceType;
	const projectUrn = currentProjectEditorStore.get.project.urn;

	const rows = React.useMemo(() => {
		if (!detection) return [];

		const labels = buildLayerLabels(detection.layers);
		const counts = new Map<string, number>();
		for (const shape of detection.shapes) {
			const owner = shapeOwner(categories, shape.layer, shape.index);
			if (owner) counts.set(owner.key, (counts.get(owner.key) ?? 0) + 1);
		}

		return categories
			.map(category => ({
				category,
				count: counts.get(category.key) ?? 0,
				label: readableLayerName(labels.get(category.baseLayer) === category.baseLayer ? category.label : labels.get(category.baseLayer) ?? category.label),
			}))
			.filter(row => row.count)
			.sort((a, b) => b.count - a.count);
	}, [detection, categories]);

	const toggle = (category: PlanCategory) => planImportStore.updateCategory(category.key, {
		enabled: !category.enabled,
		...(!category.enabled && !category.placeTypeUrn && sharedType ? { placeTypeUrn: sharedType } : {}),
	});

	const showOwnType = (key: string) => setOwnType(new Set([...ownType, key]));

	return (
		<View gap={4} style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', paddingRight: 2 }}>
			{rows.map(({ category, count, label }) => {
				const differs = !!category.placeTypeUrn && !!sharedType && category.placeTypeUrn !== sharedType;
				const typeShown = category.enabled && (differs || ownType.has(category.key) || !category.placeTypeUrn);
				const highlighted = previewed === previewKey(category);

				return (
					<View
						key={category.key}
						gap={4}
						style={{
							padding: '4px 8px',
							borderRadius: 8,
							background: highlighted ? `color-mix(in srgb, ${tokens.color.primary} 8%, transparent)` : 'transparent',
						}}
					>
						<View row gap={8} style={{ alignItems: 'center' }}>
							<input
								type="checkbox"
								checked={category.enabled}
								aria-label={`Importer ${label}`}
								onChange={() => toggle(category)}
							/>
							<button
								type="button"
								title="Montrer ces formes sur le plan"
								onClick={() => onPreview(previewKey(category))}
								style={{
									flex: 1,
									minWidth: 0,
									textAlign: 'left',
									font: 'inherit',
									fontSize: 13,
									padding: 0,
									border: 0,
									background: 'none',
									cursor: 'pointer',
									color: category.enabled ? tokens.text.default : tokens.text.secondary,
									textDecoration: category.enabled ? undefined : 'line-through',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{label}
							</button>
							<span style={{ flex: '0 0 auto', fontSize: 12, color: tokens.text.secondary }}>
								{count} forme{1 < count ? 's' : ''}
							</span>
							{category.enabled && !typeShown && (
								<Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 12 }} onClick={() => showOwnType(category.key)}>
									Autre type
								</Button>
							)}
						</View>

						{typeShown && (
							<View style={{ paddingLeft: 22 }}>
								<SelectPlaceType
									projectUrn={projectUrn}
									placeholder="Type de POI de ce calque"
									value={category.placeTypeUrn || undefined}
									onChange={(value?: unknown) => planImportStore.updateCategory(category.key, { placeTypeUrn: String(value ?? '') })}
								/>
							</View>
						)}
					</View>
				);
			})}
		</View>
	);
});

export default PlanLayerList;
